import {Asset, AssetBase, AssetTranslation} from '../../types/api';
import {apiDelete, get, list, put, PutFn, RequestOptions} from './CRUD';
import {clearCachesByTag} from '../../lib/cache';
import {AxiosRequestConfig} from 'axios';
import Api from '../../lib/apiClient.ts';
import {createFileFormData} from './UploadApi.ts';
import {getBestLocale} from '../../lib/localeHelper.ts';
import {EntityName} from './entities.ts';

function clearDependentCaches() {
    clearCachesByTag(EntityName.Profile);
    clearCachesByTag(EntityName.Kompany);
}

export const listAssets = (
    options?: RequestOptions,
    config: {
        noCache?: boolean;
    } = {},
) => list<Asset>(EntityName.Asset, options, config);

export const getAsset = (id: string) => get<Asset>(EntityName.Asset, id);

export const putAsset: PutFn<Asset> = async (id, data) => {
    const res = await put<Asset>(EntityName.Asset, id, data);

    clearDependentCaches();

    return res;
};

export async function postAsset(
    file: File,
    config?: AxiosRequestConfig,
): Promise<Asset> {
    const form = await createFileFormData({file});

    const data = (
        await Api.post(EntityName.Asset, form, {
            ...(config ?? {}),
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
    ).data;

    clearCachesByTag(EntityName.Asset);

    return data;
}

export async function postAssetTranslation(
    assetId: string,
    data: Pick<AssetTranslation, 'name' | 'locale'>,
    file: File,
    config?: AxiosRequestConfig,
): Promise<AssetTranslation> {
    const form = await createFileFormData({
        file,
    });
    form.append('asset', `/assets/${assetId}`);
    form.append('name', data.name);
    form.append('locale', data.locale);

    const r = (
        await Api.post(EntityName.AssetTranslation, form, {
            ...(config ?? {}),
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
    ).data;

    clearCachesByTag(EntityName.Asset);

    return r;
}

export const putAssetTranslation: PutFn<AssetTranslation> = async (
    id,
    data,
) => {
    const res = await put<AssetTranslation>(
        EntityName.AssetTranslation,
        id,
        data,
    );

    clearCachesByTag(EntityName.Asset);
    clearDependentCaches();

    return res;
};

export const deleteAsset = async (id: string) => {
    await apiDelete(EntityName.Asset, id);
    clearDependentCaches();
};

export const deleteAssetTranslation = async (id: string) => {
    await apiDelete(EntityName.AssetTranslation, id);
    clearCachesByTag(EntityName.Asset);
    clearDependentCaches();
};

export function getBestTranslatedAsset(
    asset: Asset,
    languages?: readonly string[],
): AssetBase {
    if (!asset.translations || asset.translations.length === 0) {
        return asset;
    }

    const availableLocales = asset.translations.map(t => t.locale);
    const l = getBestLocale(availableLocales, languages);

    return asset.translations.find(t => t.locale === l) ?? asset;
}
